import { unknown } from "superstruct"
import { getCompanyNameKey, getUserId } from "../sessionUtils"
import * as http from "./v3Http"
import type {
  CreateNewsPostReactionParams,
  DeleteNewsPostReactionParams,
  GetBundleProductsParams,
  GetCategoryProgressParams,
  GetContentInCategoryParams,
  GetGroupParams,
  GetKpiSalesParams,
  GetNewsPostParams,
  GetNewsPostsParams,
  GetProductCourseParams,
  GetProductParams,
  GetProductsParams,
  UpdateActivityCompletionParams,
  UpdateActivityItemParams,
  UpdateUserSettingsParams,
  GetNotificationsParams,
  MarkNotificationsAsReadParams,
  GetChatUsersParams,
  GetSharedItemsParams,
} from "./v3Params"
import {
  ActivityItemsResponse,
  AiAssistantConversationResponse,
  AiAssistantConversationsResponse,
  AiAssistantEventsResponse,
  AiAssistantMessageAcceptedResponse,
  AiAssistantMessagesResponse,
  ActivityProgressResponse,
  BundleProductsResponse,
  BundleResponse,
  BundleStatusResponse,
  CategoryContentResponse,
  CompanyResponse,
  CompanySettingsResponse,
  CreateNewsPostReactionResponse,
  DepartmentsResponse,
  GroupResponse,
  GroupUsersResponse,
  KpiSalesResponse,
  NewsPostResponse,
  NewsPostsResponse,
  ProductCategoryProgressResponse,
  ProductCourseResponse,
  ProductDeadlineResponse,
  ProductProgressResponse,
  ProductResponse,
  ProductsResponse,
  UpdateActivityCompletionResponse,
  UpdateActivityItemResponse,
  UserProductResponse,
  UserResponse,
  UserSettingsResponse,
  NotificationsResponse,
  ChatUsersResponse,
  SharedItemsResponse,
} from "./v3Responses"

export const getCurrentUser = async () => {
  return http.get(UserResponse, `/users/current`)
}

export const getCurrentCompany = async () => {
  const companyName = await getCompanyNameKey()
  return http.get(CompanyResponse, `/companies/${companyName}`)
}

export const getCurrentCompanySettings = async () => {
  const companyName = await getCompanyNameKey()
  return http.get(CompanySettingsResponse, `/companies/${companyName}/settings`)
}

export const getNewsPosts = async (params: GetNewsPostsParams = {}) => {
  const companyName = await getCompanyNameKey()
  const sort = "-published_at"
  const withInclude = { include: "image,reactions", sort, ...params }
  return http.get(
    NewsPostsResponse,
    `/companies/${companyName}/posts`,
    withInclude
  )
}

export const getNewsPost = (
  newsPostId: string,
  params: GetNewsPostParams = {}
) => {
  const include = ["image", ...(params.include ?? [])].join(",")
  const merged = { ...params, include }
  return http.get(NewsPostResponse, `/posts/${newsPostId}`, merged)
}

export const createNewsPostReaction = ({
  news_post_id,
  reaction_type,
}: CreateNewsPostReactionParams) => {
  const data = { reaction_type: reaction_type }
  const path = `/posts/${news_post_id}/reactions`
  return http.post(CreateNewsPostReactionResponse, path, data)
}

export const deleteNewsPostReaction = ({
  news_post_id,
  reaction_id,
}: DeleteNewsPostReactionParams) => {
  return http.del(`/posts/${news_post_id}/reactions/${reaction_id}`)
}

export const getActivityItems = (productId: number) => {
  return http.get(ActivityItemsResponse, `products/${productId}/activity_items`)
}

export const getActivityProgress = async (productId: number) => {
  const user_id = await getUserId()
  const url = `users/${user_id}/products/${productId}/activity_progress`
  return http.get(ActivityProgressResponse, url)
}

export const getCurrentUserProduct = async (productId: number) => {
  const user_id = await getUserId()
  const url = `users/${user_id}/products/${productId}`
  return http.get(UserProductResponse, url)
}

export const updateActivityItem = async ({
  product_id,
  ...params
}: UpdateActivityItemParams) => {
  const user_id = await getUserId()
  const url = `products/${product_id}/activity_item_users`
  return http.post(UpdateActivityItemResponse, url, { user_id, ...params })
}

export const updateActivityCompletion = async ({
  product_id,
  activity_item_id,
  note,
  photo,
}: UpdateActivityCompletionParams) => {
  const url = `products/${product_id}/activity_items/${activity_item_id}/completion`
  const formData = new FormData()

  if (note !== undefined) {
    formData.set("data[attributes][note]", note)
  }

  if (photo !== undefined) {
    if (photo === null) {
      formData.set("data[attributes][photo]", "")
    } else {
      formData.set("data[attributes][photo]", photo, photo.name)
    }
  }

  return http.patchMultipart(UpdateActivityCompletionResponse, url, formData)
}

export const getProduct = (
  // This reconciles V1 (id === number) and V3 (id === string)
  productId: number | string,
  { include }: GetProductParams = {}
) => {
  const params = { include: include?.join(",") }
  return http.get(ProductResponse, `products/${productId}`, params)
}

export const getProductProgress = async (productId: number) => {
  const user_id = await getUserId()
  const url = `users/${user_id}/products/${productId}/progress`
  return http.get(ProductProgressResponse, url)
}

export const getProductDeadline = async (productId: number) => {
  const user_id = await getUserId()
  const url = `users/${user_id}/products/${productId}/deadline`
  return http.get(ProductDeadlineResponse, url)
}

export const getProductCourse = (
  course_id: string,
  { include }: GetProductCourseParams = {}
) => {
  const params = { include: include?.join(",") }
  return http.get(ProductCourseResponse, `courses/${course_id}`, params)
}

export const getGroup = (
  group_id: string,
  { include }: GetGroupParams = {}
) => {
  const params = { include: include?.join(",") }
  return http.get(GroupResponse, `groups/${group_id}`, params)
}

export const getGroupUsers = (group_id: string) => {
  return http.get(GroupUsersResponse, `groups/${group_id}/users`)
}

// Chat directory: contactable colleagues for the new-conversation picker, and
// `filter[ids]` to resolve known peers (conversation list + header) by id.
// GET /api/v3/chat/users — department-scoped, 404 when chat is disabled.
export const getChatUsers = (params: GetChatUsersParams = {}) => {
  return http.get(ChatUsersResponse, `/chat/users`, params)
}

// Shared items: resolve a batch of product ids to card metadata, per-caller
// authorized, in every company (no Chat needed). Ids the caller cannot open are
// omitted, and the response is ordered by id, not by the requested order
// (BACKINT-642).
// GET /api/v3/shared_items
export const getSharedItems = (params: GetSharedItemsParams = {}) => {
  return http.get(SharedItemsResponse, `/shared_items`, params)
}

export const joinCourse = async (course_id: string) => {
  const user_id = await getUserId()
  return http.post(ProductCourseResponse, `courses/${course_id}/attendees`, {
    user_id,
  })
}

export const leaveCourse = async (course_id: string) => {
  const user_id = await getUserId()
  return http.del(`courses/${course_id}/attendees/${user_id}`, { user_id })
}

export const getProductCategoryProgress = async (
  categoryId: number,
  { include }: GetCategoryProgressParams = {}
) => {
  const userId = await getUserId()
  const path = `users/${userId}/product_categories/${categoryId}/progress`
  const params = { include: include?.join(",") }
  return http.get(ProductCategoryProgressResponse, path, params)
}

export const getMyKpiSales = async (params: GetKpiSalesParams) => {
  const userId = await getUserId()
  const path = `users/${userId}/kpi_sales`
  return http.get(KpiSalesResponse, path, params)
}

export const getDepartmentKpiSales = async (
  departmentId: string,
  params: GetKpiSalesParams
) => {
  const path = `groups/${departmentId}/kpi_sales`
  return http.get(KpiSalesResponse, path, params)
}

export const getUserDepartments = async () => {
  const userId = await getUserId()
  return http.get(DepartmentsResponse, `users/${userId}/departments`)
}

export const getProducts = async (params: GetProductsParams) => {
  const userId = await getUserId()
  return http.get(ProductsResponse, `users/${userId}/products`, params)
}

export const getContentInCategory = async ({
  include,
  categoryId,
  ...rest
}: GetContentInCategoryParams) => {
  const userId = await getUserId()
  const path = `users/${userId}/product_categories/${categoryId}/contents`
  const params = { include: include?.join(","), ...rest }
  return http.get(CategoryContentResponse, path, params)
}

export const getBundle = async (bundleId: string) => {
  const path = `/product_bundles/${bundleId}`
  return http.get(BundleResponse, path)
}

export const getBundleProducts = async (params: GetBundleProductsParams) => {
  const userId = await getUserId()
  const path = `users/${userId}/product_bundles/${params.bundleId}/products`
  return http.get(BundleProductsResponse, path, params)
}

export const getBundleProgress = async (bundleId: string) => {
  const userId = await getUserId()
  const path = `users/${userId}/product_bundles/${bundleId}/progress`
  return http.get(BundleStatusResponse, path)
}

export const getUserSettings = async () => {
  const userId = await getUserId()
  return http.get(UserSettingsResponse, `users/${userId}/settings`)
}

export const updateUserSettings = async (data: UpdateUserSettingsParams) => {
  const userId = await getUserId()
  return http.patch(UserSettingsResponse, `users/${userId}/settings`, data)
}

export const anonymizeCurrentUser = async () => {
  const userId = await getUserId()
  return http.post(unknown(), `users/${userId}/anonymize`)
}

export const getNotifications = async (params: GetNotificationsParams = {}) => {
  return http.get(NotificationsResponse, `/notifications`, params)
}

export const markNotificationsAsRead = async (
  params: MarkNotificationsAsReadParams
) => {
  return http.patch(unknown(), `/notifications/mark_as_read`, params)
}

// Engage learner chat on the AI Gateway (ENGMAN-378). A turn is accepted with a
// 202 and answered in the background; the client polls the event log from the
// cursor it was handed until `meta.done`.
const aiAssistantPath = async (assistantId: string) => {
  const companyName = await getCompanyNameKey()
  return `/companies/${companyName}/ai_assistants/${assistantId}`
}

// Newest created first.
export const getAiAssistantConversations = async (assistantId: string) => {
  const path = await aiAssistantPath(assistantId)
  return http.get(AiAssistantConversationsResponse, `${path}/conversations`)
}

export const createAiAssistantConversation = async (assistantId: string) => {
  const path = await aiAssistantPath(assistantId)
  return http.post(AiAssistantConversationResponse, `${path}/conversations`)
}

// Newest first; the caller reverses it for display.
export const getAiAssistantMessages = async (
  assistantId: string,
  conversationId: string
) => {
  const path = await aiAssistantPath(assistantId)
  return http.get(
    AiAssistantMessagesResponse,
    `${path}/conversations/${conversationId}/messages`
  )
}

// 409 when another turn in this conversation is still being answered.
export const createAiAssistantMessage = async (
  assistantId: string,
  conversationId: string,
  content: string
) => {
  const path = await aiAssistantPath(assistantId)
  return http.post(
    AiAssistantMessageAcceptedResponse,
    `${path}/conversations/${conversationId}/messages`,
    { content }
  )
}

export const getAiAssistantEvents = async (
  assistantId: string,
  conversationId: string,
  after: number
) => {
  const path = await aiAssistantPath(assistantId)
  return http.get(
    AiAssistantEventsResponse,
    `${path}/conversations/${conversationId}/events`,
    { after }
  )
}
